"use client";

import { useContext, forwardRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { sharedInputCss } from "../utils/sharedCss";
import { ValidityStateContext, SmallContext, IdContext, DescribedByContext, TypeContext, DisabledContext } from "../utils/context";
import { AppearanceContext } from "@churchofjesuschrist/eden-appearance";
import { jsx as _jsx } from "react/jsx-runtime";
export const InternalUseOnlyInput = styled.input.withConfig({
  componentId: "sc-cizuhu-0"
})(["", ";margin:0;&[type]{appearance:none;box-sizing:border-box;}"], sharedInputCss);

/**
 * The Input component allows users to input data into a form.
 */
const Input = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    "aria-describedby": ariaDescribedBy,
    id,
    small,
    type = "text",
    disabled,
    ...props
  } = _ref;
  const validityStateContext = useContext(ValidityStateContext);
  const smallContext = useContext(SmallContext);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  const curAppearanceContext = useContext(AppearanceContext);
  const typeContext = useContext(TypeContext);
  const disabledContext = useContext(DisabledContext);
  return /*#__PURE__*/_jsx(InternalUseOnlyInput, {
    "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
    id: id ? id : idContext,
    ref: ref,
    $small: small ? small : smallContext,
    disabled: disabled ? disabled : disabledContext,
    type: typeContext ?? type,
    $validityState: validityStateContext,
    $curAppearanceContext: curAppearanceContext,
    ...props
  });
});
Input.displayName = "Input";
Input.propTypes = {
  /** Identifies the element (or elements) that describes the input */
  "aria-describedby": PropTypes.string,
  /** Specify what if any permission the user agent has to provide automated assistance in filling out form field values [docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete) */
  autoComplete: PropTypes.string,
  /** Default value for uncontrolled use */
  defaultValue: PropTypes.string,
  /** Makes the input not mutable, focusable, or even submitted with the form */
  disabled: PropTypes.bool,
  /** An identifier which must be unique in the whole document */
  id: PropTypes.string,
  /** The input value must be less than or equal to the max value */
  max: PropTypes.string,
  /** The number of characters must not exceed the maxLength value */
  maxLength: PropTypes.string,
  /** The input value must be greater than or equal to the min value */
  min: PropTypes.string,
  /** The number of characters must not be less than the minLength value */
  minLength: PropTypes.string,
  /** Used to reference the input in JavaScript, or to reference form data after a form is submitted */
  name: PropTypes.string,
  /** Callback to execute when the value changes */
  onChange: PropTypes.func,
  /** A JavaScript regular expression ex:(^[A-Z].*) */
  pattern: PropTypes.string,
  /** Makes the element not mutable */
  readOnly: PropTypes.bool,
  /** Whether the input is required to be filled out to validate */
  required: PropTypes.bool,
  /** A boolean indicating if the Input should be rendered in a smaller height */
  small: PropTypes.bool,
  /** A string representing the type of input to render. If desired type is not listed, see specific components: `DateInput`, `TimeInput`, `FileInput`, etc... */
  type: PropTypes.oneOf(["color", "email", "hidden", "number", "password", "search", "tel", "text", "url"]),
  /** The value of the Input for controlled use */
  value: PropTypes.string
};
export default Input;